import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * AR cash metrics dashboard: how cash is coming in and how much of the work
 * around it Gently does, over the last 7, 14 or 30 Pacific calendar days.
 *
 * - Payment methods: Famous cash receipts split by deposit type. Famous has
 *   one code for every electronic payment, so ACH and wire are one bucket.
 * - Remittance vs bank: email remittances received in the window, whether
 *   each is linked to a bank receipt, and how far apart the dates are.
 * - Receipt filing: Famous cash receipts dated in the window, by whether
 *   Gently or the team keyed them, overall and per day.
 * - Agent: tasks the Famous cash receipt entry agent ran on in the window,
 *   and how many it escalated to a person.
 * - Check lag: days from the date on each check to the day its Famous
 *   receipt was entered.
 * - Email remittances: remittances Gently recorded from email, against the
 *   ones it missed — emails the remittance workflow recognized but failed to
 *   record, cash receipts an AR user marked as missing their remittance, and
 *   remittances a person had to add by hand.
 */

export const AR_CASH_METRICS_RANGES = ["7d", "14d", "30d"] as const;
export const ArCashMetricsRangeSchema = z.enum(AR_CASH_METRICS_RANGES);
export const DEFAULT_AR_CASH_METRICS_RANGE =
  "14d" satisfies (typeof AR_CASH_METRICS_RANGES)[number];
export const AR_CASH_METRICS_RANGE_DAYS = {
  "7d": 7,
  "14d": 14,
  "30d": 30,
} as const satisfies Record<(typeof AR_CASH_METRICS_RANGES)[number], number>;

export const AR_CASH_METRICS_TIME_ZONE = "America/Los_Angeles";

const DaySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const CountSchema = z.number().int().nonnegative();
/** Decimal string, e.g. "1234.56". */
const AmountSchema = z.string();

const CountAmountSchema = z.object({
  count: CountSchema,
  amount: AmountSchema,
});

export const ArCashMetricsCheckCustomerSchema = z.object({
  customerId: z.uuid(),
  customerName: z.string(),
  check: CountAmountSchema,
  electronic: CountAmountSchema,
  /** Checks as a % of the customer's classified receipts in the window. */
  checkSharePct: z.number().nullable(),
  lastCheckDate: DaySchema.nullable(),
});

export const ArCashMetricsPaymentMethodsSchema = z.object({
  check: CountAmountSchema,
  /** Famous EFT: ACH and wire share one deposit type. */
  electronic: CountAmountSchema,
  cash: CountAmountSchema,
  unclassified: CountAmountSchema,
  customersPayingByCheck: CountSchema,
  customersPayingElectronically: CountSchema,
  /** Customers with at least one check in the window, most check dollars first. */
  checkCustomers: z.array(ArCashMetricsCheckCustomerSchema),
  checkCustomersTruncated: z.boolean(),
});

export const AR_CASH_METRICS_DATE_GAP_BUCKETS = [
  "same_day",
  "1_3_days",
  "4_7_days",
  "8_14_days",
  "15_31_days",
  "over_31_days",
  "no_stated_date",
] as const;
export const ArCashMetricsDateGapBucketSchema = z.enum(
  AR_CASH_METRICS_DATE_GAP_BUCKETS,
);

export const ArCashMetricsRemittanceBankMatchSchema = z.object({
  /** Amounts are the remittances' stated totals. */
  emailRemittances: CountAmountSchema,
  linkedToBank: CountAmountSchema,
  /**
   * Not linked, but the bank feed has not reached the remittance's payment
   * date yet, so there is nothing to match it to. Left out of the match rate.
   */
  awaitingBankReceipt: CountAmountSchema,
  /** Not linked although the bank feed is past its payment date. */
  unmatched: CountAmountSchema,
  /**
   * Not linked, bank feed past its date, and no customer identified, so no
   * payment to link a bank receipt to.
   */
  noCustomer: CountAmountSchema,
  /**
   * Days between the remittance's stated payment date and the bank date, for
   * linked remittances. Matching is exact to the cent, so a linked remittance
   * always agrees with the bank on amount; the date is what varies.
   */
  dateGap: z.array(
    z.object({ bucket: ArCashMetricsDateGapBucketSchema, count: CountSchema }),
  ),
  medianDateGapDays: z.number().nullable(),
});

export const AR_CASH_METRICS_FILING_PAYMENT_TYPES = [
  "check",
  "electronic",
  "cash",
  "other",
] as const;

/**
 * One Famous bank account and payment type: its receipts in the range and how
 * many Gently keyed. The agent only works some accounts and payment types, so
 * the overall share understates it where it runs.
 */
export const ArCashMetricsReceiptFilingBankSchema = z.object({
  /** Famous's name for the account; null when Famous didn't record one. */
  bankAccountName: z.string().nullable(),
  /** Decoded Famous deposit type; ACH and wire share one code. */
  paymentType: z.enum(AR_CASH_METRICS_FILING_PAYMENT_TYPES),
  receipts: CountAmountSchema,
  byGently: CountAmountSchema,
  /**
   * The same counts for customers whose remit type is email: the receipts
   * the agent can key from an emailed remittance.
   */
  fromEmailRemitters: z.object({
    receipts: CountAmountSchema,
    byGently: CountAmountSchema,
  }),
});

/**
 * Famous cash receipts dated in the range, by who keyed them, overall and per
 * bank account and payment type (busiest account first, then its busiest
 * type). The agent stamps each receipt it keys
 * with an "entry by Gently" description; every other Famous receipt was keyed
 * by the team.
 */
export const ArCashMetricsReceiptFilingSchema = z.object({
  receipts: CountAmountSchema,
  byGently: CountAmountSchema,
  byTeam: CountAmountSchema,
  byBankAndType: z.array(ArCashMetricsReceiptFilingBankSchema),
});

/** Famous receipt counts for one day of the range, by who keyed them. */
export const ArCashMetricsReceiptFilingDaySchema = z.object({
  day: DaySchema,
  byGently: CountSchema,
  byTeam: CountSchema,
});

export const ArCashMetricsEscalationSchema = z.object({
  taskId: z.uuid(),
  cashReceiptId: z.uuid().nullable(),
  escalatedAt: z.string(),
  question: z.string().nullable(),
  customerName: z.string().nullable(),
  amount: AmountSchema.nullable(),
});

export const ArCashMetricsAgentSchema = z.object({
  /** Distinct tasks the agent ran on in the window. */
  attempted: CountSchema,
  /** Of those, tasks the agent escalated to a person. */
  escalated: CountSchema,
  /** Of those, tasks the agent itself closed as keyed. */
  keyed: CountSchema,
  /** The latest escalation of each of the most recently escalated tasks. */
  recentEscalations: z.array(ArCashMetricsEscalationSchema),
});

export const ArCashMetricsMissedEmailSchema = z.object({
  inboundEmailId: z.uuid(),
  subject: z.string().nullable(),
  sender: z.string().nullable(),
  receivedAt: z.string(),
});

export const ArCashMetricsMarkedReceiptSchema = z.object({
  cashReceiptId: z.uuid(),
  customerName: z.string().nullable(),
  amount: AmountSchema.nullable(),
  transactionDate: DaySchema.nullable(),
  markedAt: z.string(),
});

export const ArCashMetricsEmailRemittancesSchema = z.object({
  /** Remittances recorded from email. */
  found: CountSchema,
  /** Emails recognized as remittances whose workflow recorded nothing. */
  failedInPipeline: CountSchema,
  /**
   * Cash receipts dated in the range that an AR user marked as missing a
   * remittance Gently should have recorded.
   */
  markedMissing: CountSchema,
  /**
   * Remittances a person added by hand, unless their receipt is marked
   * missing (already counted there).
   */
  addedManually: CountSchema,
  recentFailures: z.array(ArCashMetricsMissedEmailSchema),
  recentMarkedMissing: z.array(ArCashMetricsMarkedReceiptSchema),
});

export const GetArCashMetricsInputSchema = z.object({
  range: ArCashMetricsRangeSchema.prefault(DEFAULT_AR_CASH_METRICS_RANGE),
});

/** Days from the date on a check to the day its receipt was entered. */
export const AR_CASH_METRICS_CHECK_LAG_BUCKETS = [
  "same_day",
  "1_3_days",
  "4_7_days",
  "8_14_days",
  "over_14_days",
] as const;
export const ArCashMetricsCheckLagBucketSchema = z.enum(
  AR_CASH_METRICS_CHECK_LAG_BUCKETS,
);

/**
 * How long checks take to arrive: for Famous check receipts dated in the
 * range, days from the date on the check (BANK_DEPOSIT_LINE.REFDATE) to the
 * receipt date. A check dated after its receipt is almost certainly a typo,
 * so it is counted apart and left out of the median and buckets.
 */
export const ArCashMetricsCheckLagSchema = z.object({
  checks: CountSchema,
  withCheckDate: CountSchema,
  checkDateAfterReceipt: CountSchema,
  medianDays: z.number().nullable(),
  p90Days: z.number().nullable(),
  buckets: z.array(
    z.object({ bucket: ArCashMetricsCheckLagBucketSchema, count: CountSchema }),
  ),
});

export const GetArCashMetricsResponseSchema = z.object({
  timeZone: z.literal(AR_CASH_METRICS_TIME_ZONE),
  range: ArCashMetricsRangeSchema,
  from: DaySchema,
  to: DaySchema,
  paymentMethods: ArCashMetricsPaymentMethodsSchema,
  remittanceBankMatch: ArCashMetricsRemittanceBankMatchSchema,
  receiptFiling: ArCashMetricsReceiptFilingSchema,
  /** Every day of the range, oldest first, including days with no receipts. */
  receiptFilingDaily: z.array(ArCashMetricsReceiptFilingDaySchema),
  agent: ArCashMetricsAgentSchema,
  emailRemittances: ArCashMetricsEmailRemittancesSchema,
  checkLag: ArCashMetricsCheckLagSchema,
});

export const arCashMetricsContracts = {
  get: oc
    .input(GetArCashMetricsInputSchema)
    .output(GetArCashMetricsResponseSchema)
    .route({
      method: "GET",
      summary: "Get AR cash metrics",
      description:
        "Returns cash-intake statistics over 7, 14, or 30 days: check versus electronic payments by customer, how closely email remittances agree with bank receipts, the share of keyed cash receipts filed by Gently (overall, per bank account and payment type, and per day), the cash receipt agent's escalation rate, email remittances found versus missed, and the lag from check date to receipt date.",
    }),
} as const;

export type ArCashMetricsRange = z.infer<typeof ArCashMetricsRangeSchema>;
export type ArCashMetricsCheckLagBucket = z.infer<
  typeof ArCashMetricsCheckLagBucketSchema
>;
export type ArCashMetricsDateGapBucket = z.infer<
  typeof ArCashMetricsDateGapBucketSchema
>;
export type ArCashMetricsCheckCustomer = z.infer<
  typeof ArCashMetricsCheckCustomerSchema
>;
export type ArCashMetricsReceiptFilingBank = z.infer<
  typeof ArCashMetricsReceiptFilingBankSchema
>;
export type ArCashMetricsReceiptFilingDay = z.infer<
  typeof ArCashMetricsReceiptFilingDaySchema
>;
export type ArCashMetricsPaymentMethods = z.infer<
  typeof ArCashMetricsPaymentMethodsSchema
>;
export type GetArCashMetricsResponse = z.infer<
  typeof GetArCashMetricsResponseSchema
>;
